import { Component } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';

/**
 * Side menu shell component.
 * Renders header and content slots for a reusable drawer layout.
 */
@Component({
    selector: 'upp-side-menu',
    templateUrl: './upp-side-menu.html',
    styleUrls: ['./upp-side-menu.scss'],
})
export class UppSideMenuComponent {
    // visual container only
}

/**
 * Header slot wrapper for the side menu shell.
 */
@Component({
    selector: 'upp-side-menu-header',
    template: `<ng-content></ng-content>`,
    styleUrls: [],
})
export class UppSideMenuHeaderComponent {
    // slot wrapper
}

/**
 * Header toggle action used inside the side menu header.
 */
@Component({
    selector: 'upp-side-menu-header-toggle',
    template: `
        <ion-button
            class="side-menu-toggle"
            fill="clear"
            [color]="color"
            [disabled]="disabled"
            (click)="OnToggle($event)"
        >
            <ion-icon [name]="icon"></ion-icon>
        </ion-button>
    `,
    styleUrls: ['./upp-side-menu.scss'],
})
export class UppSideMenuHeaderToggleComponent {
    /** Icon name rendered inside the toggle button. */
    @Input() icon = 'menu-outline';
    /** Ionic color token applied to the toggle button. */
    @Input() color = 'medium';
    /** Disables the toggle button when true. */
    @Input() disabled = false;

    /** Emitted when the header toggle is clicked. */
    @Output() Toggled = new EventEmitter<MouseEvent>();

    /**
     * Emits the toggle click event to parent containers.
     * @param {MouseEvent} event Native click event from the toggle button.
     * @returns {void}
     */
    OnToggle(event: MouseEvent){
        this.Toggled.emit(event);
    }
}

/**
 * Content slot wrapper for grouped side menu sections.
 */
@Component({
    selector: 'upp-side-menu-content',
    template: `<ng-content></ng-content>`,
    styleUrls: [],
})
export class UppSideMenuContentComponent {
    // slot wrapper
}

/**
 * Section wrapper that optionally renders a title above projected actions.
 */
@Component({
    selector: 'upp-side-menu-section',
    template: `
        <div class="side-menu-section">
            <ion-item *ngIf="title" class="side-menu-section-title" lines="none">
                <ion-label>{{ title }}</ion-label>
            </ion-item>
            <ng-content></ng-content>
        </div>
    `,
    styleUrls: ['./upp-side-menu.scss'],
})
export class UppSideMenuSectionComponent {
    /** Optional section title shown as a header row. */
    @Input() title: string | null = null;
}

/**
 * Clickable action row used for each entry in the side menu.
 */
@Component({
    selector: 'upp-side-menu-action',
    template: `
        <ion-item
            class="side-menu-action"
            button
            detail="false"
            [disabled]="disabled"
            [lines]="lines"
            [color]="RowColor"
            (click)="OnClick($event)"
        >
            <ion-icon *ngIf="icon" [slot]="iconSlot" [name]="icon" [color]="iconColor"></ion-icon>
            <ion-label [color]="labelColor || iconColor">{{ label }}</ion-label>
            <ng-content></ng-content>
        </ion-item>
    `,
    styleUrls: ['./upp-side-menu.scss'],
})
export class UppSideMenuActionComponent {
    /** Icon name rendered on the left side of the action row. */
    @Input() icon: string | null = null;
    /**
     * Ionic color applied to the whole row (background + contrast text/icon),
     * forwarded to the inner `ion-item`. Takes precedence over `active`/`activeColor`.
     * Leave null for the default transparent row.
     */
    @Input() color: string | null = null;
    /** Ionic color applied to the icon; also used by label when labelColor is null. */
    @Input() iconColor: string | null = null;
    /** Slot position for icon rendering inside the row. */
    @Input() iconSlot: 'start' | 'end' = 'start';
    /** Ionic color applied to the action label text. */
    @Input() labelColor: string | null = null;
    /** Action label text. */
    @Input() label = '';
    /** Disables click interaction when true. */
    @Input() disabled = false;
    /** Marks the action as active and applies activeColor to the row. */
    @Input() active = false;
    /** Row color applied when active is true. */
    @Input() activeColor = 'light';
    /** Ionic line style for the row separator. */
    @Input() lines: 'none' | 'full' = 'none';

    /** Emitted when the action row is clicked. */
    @Output() Clicked = new EventEmitter<MouseEvent>();

    /**
     * Ionic color applied to the row: explicit `color` wins; otherwise `activeColor`
     * while `active` is true; otherwise none (transparent default).
     */
    get RowColor(): string | null {
        return this.color ?? (this.active ? this.activeColor : null);
    }

    /**
     * Emits the action click event to parent containers.
     * @param {MouseEvent} event Native click event from the action row.
     * @returns {void}
     */
    OnClick(event: MouseEvent){
        this.Clicked.emit(event);
    }
}

